// react
import React, { useState, useEffect } from "react";

// react-router-dom
import { useNavigate, useParams } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, Box, Tabs, Tab, CircularProgress, Card, useTheme } from "@mui/material";

// ui-components
import TagToolbar from "ui-components/databox/TagToolbar";
import CertificationMenu from "ui-components/certification/CertificationMenu";
import AggregatedViewer from "ui-components/DataTypeManagement/History/AggregatedViewer";
import DocViewer from "ui-components/DataTypeManagement/Doc/DocViewer";
import ImageViewer from "ui-components/DataTypeManagement/Image/ImageViewer";
import InfoViewer from "ui-components/DataTypeManagement/Info/InfoViewer";
import ConfigViewer from "ui-components/DataTypeManagement/Config/ConfigViewer";
import MpsViewer from "ui-components/DataTypeManagement/Mps/MpsViewer";
import AlarmViewer from "ui-components/DataTypeManagement/Alarms/AlarmViewer";
import SensorDataAggregatedViewer from "ui-components/DataTypeManagement/SensorDataAggregated/SensorDataAggregatedViewer";
import LikeSirtiViewer from "ui-components/DataTypeManagement/Custom/LikeSirti/LikeSirtiViewer";
import CDNFileViewer from "ui-components/DataTypeManagement/CDNFile/CDNFileViewer";
import DocGenerator from "ui-components/DataTypeManagement/Doc/DocGenerator";
import ImageGenerator from "ui-components/DataTypeManagement/Image/ImageGenerator";
import InfoGenerator from "ui-components/DataTypeManagement/Info/InfoGenerator";
import ConfigGenerator from "ui-components/DataTypeManagement/Config/ConfigGenerator";
import CDNFileGenerator from "ui-components/DataTypeManagement/CDNFile/CDNFileGenerator";
import { CertificationSuccessful, CertificationError, DataboxNotFound, DataBoxFound } from "ui-components/ORFeedbacks";

// firebase
import { db } from "config/firebase";
import { doc, onSnapshot } from "firebase/firestore";

// databox-controller
import getTag from "databox-controller/getTag";
import getTagOwner from "databox-controller/getTagOwner";
import getTagTypes from "databox-controller/getTagTypes";
import isUserFollowingDatabox from "databox-controller/isUserFollowingDatabox";
import { addFollower, removeFollower } from "databox-controller/manageFollower";
import { addAlarmUser, removeAlarmUser, hasUserAlarmOn } from "databox-controller/manageAlarm";

// user-controller
import isUserAuthorizedForDataBox from "user-controller/isUserAuthorizedForDataBox";
import isUserAuthorizedForDataBoxReadOnly from "user-controller/isUserAuthorizedForDataBoxReadOnly";
import getSeenTag from "user-controller/getSeenTag";
import getUserContacts from "user-controller/getUserContacts";

// A ---------------------------------------------------------------------- M

const Databox = () => {
  const { user, conservSostL1, canSign, seedflow } = UserAuth();
  const { tag } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();

  // Stati principali
  const [databox, setDatabox] = useState(null);
  const [seenTag, setSeenTag] = useState(null);
  const [isDataboxFound, setIsDataboxFound] = useState(false);
  const [tagNotExist, setTagNotExist] = useState(false);
  const [tagConf, setTagConf] = useState(false);
  const [types, setTypes] = useState([]);
  const [isTagGroupMember, setIsTagGroupMember] = useState(false);
  const [isTagGroupMemberReadOnly, setIsTagGroupMemberReadOnly] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [alarmOn, setAlarmOn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [request, setRequest] = useState();

  // Stati per i drawer
  const [openDocGen, setOpenDocGen] = useState(false);
  const [openImageGen, setOpenImageGen] = useState(false);
  const [openInfoGen, setOpenInfoGen] = useState(false);
  const [openUploadGen, setOpenUploadGen] = useState(false);
  const [openConfigurationGen, setOpenConfigurationGen] = useState(false);
  const [openCertificationSuccessful, setOpenCertificationSuccessful] = useState(false);
  const [openCertificationError, setOpenCertificationError] = useState(false);

  // Stati per le tabs
  const [value, setValue] = useState(0);
  const [value1, setValue1] = useState(0);

  // Helper: apre un drawer e aggiorna la history
  const openDrawer = (setOpen) => {
    setOpen(true);
    window.history.pushState(null, "");
  };

  // Funzioni per aprire i drawer specifici
  const openDocGenDrawer = () => openDrawer(setOpenDocGen);
  const openImageGenDrawer = () => openDrawer(setOpenImageGen);
  const openInfoGenDrawer = () => openDrawer(setOpenInfoGen);
  const openUploadGenDrawer = () => openDrawer(setOpenUploadGen);
  const openConfigurationGenDrawer = () => openDrawer(setOpenConfigurationGen);
  const handleOpenCertificationSuccessful = () => openDrawer(setOpenCertificationSuccessful);
  const handleOpenCertificationError = () => openDrawer(setOpenCertificationError);
  const handleTagNotExistDrawer = () => openDrawer(setTagNotExist);
  const handleTagConfDrawer = () => openDrawer(setTagConf);

  // Effetto per gestire popstate (chiusura drawer)
  useEffect(() => {
    const handlePopstate = () => {
      if (tagNotExist) setTagNotExist(false);
      if (tagConf) setTagConf(false);
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [tagNotExist, tagConf]);

  useEffect(() => {
    let unsubscribe;

    const initDatabox = async () => {
      try {
        // 1. Databox
        const tagData = await getTag(tag);

        if (!tagData?.id) {
          handleTagNotExistDrawer();
          return;
        }

        setDatabox(tagData);

        // 2. Parallel fetch of user-related data
        const [isAuthorizedReadWrite, isAuthorizedReadOnly, isFollowing, alarmOn, seenTag] = await Promise.all([
          isUserAuthorizedForDataBox(tag, user.uid),
          isUserAuthorizedForDataBoxReadOnly(tag, user.uid),
          isUserFollowingDatabox(user.uid, tag),
          hasUserAlarmOn(user.uid, tag),
          getSeenTag(user.uid, tag),
        ]);

        setIsTagGroupMember(isAuthorizedReadWrite);
        setIsTagGroupMemberReadOnly(isAuthorizedReadOnly);
        setIsFollowing(isFollowing);
        setAlarmOn(alarmOn);

        if (!seenTag) {
          handleTagConfDrawer();
          return;
        }

        // 3. Owner info
        const tagOwner = await getTagOwner(tag);
        const tagOwnerData = tagOwner ? await getUserContacts(tagOwner) : {};

        // 4. Build user-scoped seenTag
        const updatedSeentag = {
          ...seenTag,
          tagOwner,
          tagOwnerEmail: tagOwnerData.email || "",
        };

        setSeenTag(updatedSeentag);
        setIsDataboxFound(true);

        // 5. Fetch types once
        const tagTypes = await getTagTypes(tag);
        setTypes([...new Set(tagTypes)]);

        // 6. Subscribe to databox changes (types)
        const tagsdataDocRef = doc(db, "tagsdata", tag);
        unsubscribe = onSnapshot(tagsdataDocRef, async () => {
          const updatedTypes = await getTagTypes(tag);
          setTypes([...new Set(updatedTypes)]);
        });
      } catch (error) {
        console.error("Error initializing databox:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initDatabox();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [tag, user.uid, conservSostL1]);

  // Gestori per chiusura drawer
  const handleTagNotExist = () => {
    setTagNotExist(false);
    navigate("/");
  };

  const handleCloseTagConf = () => {
    setTagConf(false);
    navigate("/");
  };

  // Toggle Follow e Alarm
  const handleFollowToggle = async () => {
    try {
      let success = false;

      if (isFollowing) {
        success = await removeFollower(user.uid, tag);
      } else {
        success = await addFollower(user.uid, tag);
      }

      if (success) {
        setIsFollowing((prev) => !prev);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Follow toggle failed:", error);
      return false;
    }
  };

  const handleAlarmToggle = async () => {
    try {
      let success = false;

      if (alarmOn) {
        success = await removeAlarmUser(user.uid, tag);
      } else {
        success = await addAlarmUser(user.uid, tag);
      }

      if (success) {
        setAlarmOn((prev) => !prev);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Alarm toggle failed:", error);
      return false;
    }
  };

  // Gestori per le tabs
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChange1 = (event, newValue) => {
    setValue1(newValue);
  };

  if (isLoading) {
    return (
      <Grid item container justifyContent="center" mt="30%">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      {tagNotExist && <DataboxNotFound tag={tag} isArchive={conservSostL1} onClose={handleTagNotExist} />}

      {tagConf && databox && <DataBoxFound tag={databox} open={tagConf} setOpen={setTagConf} handleClose={handleCloseTagConf} />}

      {isDataboxFound && (
        <>
          <Grid container spacing={5}>
            {(isTagGroupMember || isTagGroupMemberReadOnly) && databox && seenTag && (
              <Grid item xs={12}>
                <TagToolbar databox={databox} tag={seenTag} isFollowing={isFollowing} handleFollowToggle={handleFollowToggle} alarmOn={alarmOn} handleAlarmToggle={handleAlarmToggle} variant="full" />
              </Grid>
            )}

            <Grid item container xs={12} spacing={2}>
              {!conservSostL1 && (
                <Grid item container>
                  {isTagGroupMember && canSign && seedflow && databox && (
                    <Grid item container xs={12} justifyContent="flex-end">
                      <CertificationMenu
                        tag={databox}
                        openDocGenDrawer={openDocGenDrawer}
                        openImageGenDrawer={openImageGenDrawer}
                        openInfoGenDrawer={openInfoGenDrawer}
                        openConfigurationGenDrawer={openConfigurationGenDrawer}
                      />
                    </Grid>
                  )}
                </Grid>
              )}

              <Grid item container xs={12} spacing={5}>
                {types.includes("sensor_data_aggregated") && databox && (
                  <Grid item xs={12}>
                    <Box p={2}>
                      <SensorDataAggregatedViewer tag={databox} isTagGroupMember={isTagGroupMember} />
                    </Box>
                  </Grid>
                )}

                {types.some((type) => ["cfg", "mps"].includes(type)) && databox && (
                  <Grid item xs={12}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        background: `linear-gradient(135deg, ${theme.palette.background.paper}CC, ${theme.palette.background.default}EE)`,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "0 4px 18px rgba(0,0,0,0.12)",
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          background: `linear-gradient(180deg, ${theme.palette.background.paper}F2, ${theme.palette.background.default}AA)`,
                          backdropFilter: "blur(8px)",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Tabs
                          value={value1}
                          onChange={handleChange1}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            "& .MuiTab-root": {
                              fontWeight: 600,
                              textTransform: "none",
                              minHeight: 42,
                              px: 1.5,
                            },
                            "& .MuiTabs-indicator": {
                              height: 3,
                              borderRadius: 2,
                            },
                          }}
                        >
                          {["cfg", "mps"]
                            .filter((type) => types.includes(type))
                            .sort()
                            .map((type) => (
                              <Tab key={type} label={type === "cfg" ? t("configurations") : t("stream")} />
                            ))}

                          <Tab label={t("alarms")} />
                        </Tabs>
                      </Box>

                      <Box sx={{ px: 2.5, py: 2 }}>
                        {["cfg", "mps"]
                          .filter((type) => types.includes(type))
                          .sort()
                          .map((type, index) => (
                            <Box key={type} sx={{ display: value1 === index ? "block" : "none" }}>
                              {type === "cfg" ? <ConfigViewer tag={databox} isTagGroupMember={isTagGroupMember} /> : <MpsViewer tag={databox} isTagGroupMember={isTagGroupMember} />}
                            </Box>
                          ))}

                        <Box
                          sx={{
                            display: value1 === ["cfg", "mps"].filter((t) => types.includes(t)).length ? "block" : "none",
                          }}
                        >
                          <AlarmViewer tag={databox} isTagGroupMember={isTagGroupMember} />
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                )}

                {types.some((type) => ["doc", "img", "info", "like_sirti", "source_telemetrics"].includes(type)) && databox && (
                  <Grid item xs={12}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        background: `linear-gradient(135deg, ${theme.palette.background.paper}CC, ${theme.palette.background.default}EE)`,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "0 4px 18px rgba(0,0,0,0.12)",
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          background: `linear-gradient(180deg, ${theme.palette.background.paper}F2, ${theme.palette.background.default}AA)`,
                          backdropFilter: "blur(8px)",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Tabs
                          value={value}
                          onChange={handleChange}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            "& .MuiTab-root": {
                              fontWeight: 600,
                              textTransform: "none",
                              minHeight: 42,
                              px: 1.5,
                            },
                            "& .MuiTabs-indicator": {
                              height: 3,
                              borderRadius: 2,
                            },
                          }}
                        >
                          {(types.includes("doc") || types.includes("img") || types.includes("info") || types.includes("like_sirti") || types.includes("source_telemetrics")) && (
                            <Tab label={t("history")} />
                          )}

                          {["doc", "img", "info", "like_sirti", "source_telemetrics"]
                            .filter((type) => types.includes(type))
                            .sort((a, b) => {
                              const order = ["doc", "img", "info", "like_sirti", "source_telemetrics"];
                              return order.indexOf(a) - order.indexOf(b);
                            })
                            .map((type) => {
                              const label = {
                                info: t("notes"),
                                doc: t("documents"),
                                img: t("images"),
                                like_sirti: "Ico-Sirti",
                                source_telemetrics: "source_telemetrics",
                              }[type];

                              return <Tab key={type} label={label} />;
                            })}
                        </Tabs>
                      </Box>

                      <Box sx={{ px: 2.5, py: 2 }}>
                        <Box sx={{ display: value === 0 ? "block" : "none" }}>
                          <AggregatedViewer tag={databox} isTagGroupMember={isTagGroupMember} />
                        </Box>

                        {["doc", "img", "info", "like_sirti", "source_telemetrics"]
                          .filter((type) => types.includes(type))
                          .sort((a, b) => {
                            const order = ["doc", "img", "info", "like_sirti", "source_telemetrics"];
                            return order.indexOf(a) - order.indexOf(b);
                          })
                          .map((type, index) => (
                            <Box key={type} sx={{ display: value === index + 1 ? "block" : "none" }}>
                              {type === "doc" && <DocViewer tag={databox} isTagGroupMember={isTagGroupMember} />}
                              {type === "img" && <ImageViewer tag={databox} isTagGroupMember={isTagGroupMember} />}
                              {type === "info" && <InfoViewer tag={databox} isTagGroupMember={isTagGroupMember} />}
                              {type === "like_sirti" && <LikeSirtiViewer tag={databox} isTagGroupMember={isTagGroupMember} />}
                            </Box>
                          ))}
                      </Box>
                    </Card>
                  </Grid>
                )}

                {conservSostL1 && (isTagGroupMember || isTagGroupMemberReadOnly) && databox && (
                  <Grid item xs={12}>
                    <CDNFileViewer tag={databox} isTagGroupMember={isTagGroupMember || isTagGroupMemberReadOnly} openUploadGenDrawer={openUploadGenDrawer} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>

          {openDocGen && (
            <DocGenerator
              tag={tag}
              types={types}
              setTypes={setTypes}
              open={openDocGen}
              setOpen={setOpenDocGen}
              handleOpenCertificationSuccessful={handleOpenCertificationSuccessful}
              handleOpenCertificationError={handleOpenCertificationError}
              request={request}
              setRequest={setRequest}
            />
          )}

          {openImageGen && (
            <ImageGenerator
              tag={tag}
              types={types}
              setTypes={setTypes}
              open={openImageGen}
              setOpen={setOpenImageGen}
              handleOpenCertificationSuccessful={handleOpenCertificationSuccessful}
              handleOpenCertificationError={handleOpenCertificationError}
              request={request}
              setRequest={setRequest}
            />
          )}

          {openInfoGen && (
            <InfoGenerator
              tag={tag}
              types={types}
              setTypes={setTypes}
              open={openInfoGen}
              setOpen={setOpenInfoGen}
              handleOpenCertificationSuccessful={handleOpenCertificationSuccessful}
              handleOpenCertificationError={handleOpenCertificationError}
              request={request}
              setRequest={setRequest}
            />
          )}

          {openConfigurationGen && databox?.targets?.length > 0 && (
            <ConfigGenerator
              tag={tag}
              targets={databox.targets}
              types={types}
              setTypes={setTypes}
              open={openConfigurationGen}
              setOpen={setOpenConfigurationGen}
              handleOpenCertificationSuccessful={handleOpenCertificationSuccessful}
              handleOpenCertificationError={handleOpenCertificationError}
              request={request}
              setRequest={setRequest}
            />
          )}

          {openUploadGen && conservSostL1 && (
            <CDNFileGenerator
              tag={databox}
              types={types}
              setTypes={setTypes}
              open={openUploadGen}
              setOpen={setOpenUploadGen}
              handleOpenCertificationSuccessful={handleOpenCertificationSuccessful}
              handleOpenCertificationError={handleOpenCertificationError}
            />
          )}

          {openCertificationSuccessful && <CertificationSuccessful open={openCertificationSuccessful} setOpen={setOpenCertificationSuccessful} />}

          {openCertificationError && <CertificationError open={openCertificationError} setOpen={setOpenCertificationError} />}
        </>
      )}
    </>
  );
};

export default Databox;
